import _ from 'lodash'

// Destination for logout / auth-failure redirects.
//
// In production the dashboard is the public site at https://castr.com/app/
// (castr.com/app is reverse-proxied to the Cloudflare Pages deploy). We must
// force that absolute URL: a relative path would leave the user on the internal
// `dashboard.castr.com` host (which we don't want exposed), and a root `/signin`
// doesn't exist (the app lives under /app/). Non-prod stays relative so it works
// on staging / localhost.
export function getSigninUrl() {
	return process.env.VUE_APP_API_ENV === 'prod'
		? 'https://castr.com/app/signin'
		: '/app/login'
}

// Destination once a session is established and we want a full reload rather
// than a router push. Same reasoning as above, and the same two reasons a bare
// '/' is wrong: in production it is the marketing site, not the dashboard, and
// a relative path keeps the user on `dashboard.castr.com`.
export function getDashboardUrl() {
	return process.env.VUE_APP_API_ENV === 'prod'
		? 'https://castr.com/app/dashboard'
		: '/app/dashboard'
}

// Real guest/auth routes only — never a substring match. `/app/logintest`
// used to look like `/login` because `href` was tested with /login|signin|signup/.
const AUTH_PATH_RE = /^\/(?:login|signin|signup|admin-login|signin-22787|login-redirect|logout-redirect|forgot-password(?:\/reset)?|invite-link\/[^/]+|two-factor-verification)\/?$/i

export function getAppPathname(href) {
	const raw = href || (typeof window !== 'undefined' ? window.location.href : '')
	let pathname = '/'
	try {
		pathname = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'https://castr.com').pathname
	} catch {
		pathname = typeof window !== 'undefined' ? window.location.pathname : '/'
	}
	pathname = pathname.replace(/\/+$/, '') || '/'
	if (pathname === '/app') return '/'
	if (pathname.startsWith('/app/')) return pathname.slice(4)
	return pathname
}

export function isAuthLocation(href) {
	return AUTH_PATH_RE.test(getAppPathname(href))
}

// Reproduces the redirect contract from the legacy WordPress sign-in/sign-up
// pages: send the user into the Vue app at /app/<path>, honoring a `continue`
// query param, forwarding `_`-prefixed params, and handling team `join` links.
//
// `query` (optional) is a parsed query object (e.g. Vue Router `to.query`); when
// omitted we parse `window.location.search`. Passing it explicitly lets callers
// like the router guard honor params on the *target* route during in-app
// navigations, not just on a full page load.
export function redirectAfterAuth({ signup = false, query = null } = {}) {
	let redirectPath = signup ? 'dashboard?success=true' : 'dashboard'
	let qs = '?'

	const searchParams = {}
	if (query) {
		_.each(query, (rawValue, key) => {
			// Router query values can be arrays when a key repeats; take the first.
			const value = _.isArray(rawValue) ? rawValue[0] : rawValue
			if (key === 'continue') {
				searchParams[key] = value
			} else if (/^_/g.test(key)) {
				qs += `${_.replace(key, /^_/g, '')}=${value}&`
			}
			if (key === 'join') {
				searchParams[key] = value
			}
		})
	} else {
		const search = window.location.search || ''
		if (search) {
			_.each(_.split(search, '&'), (chunk) => {
				const pars = _.split(chunk, '=')
				let key = pars[0]
				const value = pars[1]
				key = _.replace(key, /^\?/g, '')

				if (key === 'continue') {
					searchParams[key] = value
				} else if (/^_/g.test(key)) {
					key = _.replace(key, /^_/g, '')
					qs += `${key}=${value}&`
				}
				if (key === 'join') {
					searchParams[key] = value
				}
			})
		}
	}

	redirectPath = searchParams.continue || redirectPath
	qs = _.replace(qs, /(\?|&)$/gi, '')

	if (searchParams.join) {
		const teamId = _.replace(searchParams.join, /(\?|&)$/gi, '')
		redirectPath += signup
			? `&from=signup&teamID=${teamId}`
			: `?teamID=${teamId}`
		qs = ''
	}

	window.location.href = `${window.location.origin}/app/${redirectPath}${qs}`
}
